import React, { useState, useRef, useEffect, useLayoutEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  content: ReactNode
  children: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
  className?: string
  disabled?: boolean
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  delay = 300,
  className = '',
  disabled = false
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [actualPosition, setActualPosition] = useState(position)
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false)
  const [isHoveringTrigger, setIsHoveringTrigger] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const showTimeoutRef = useRef<NodeJS.Timeout>()
  const hideTimeoutRef = useRef<NodeJS.Timeout>()

  const showTooltip = () => {
    if (disabled || !content) return
    
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }
    
    showTimeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft
        const scrollY = window.pageYOffset || document.documentElement.scrollTop
        
        // Estimate tooltip size (max-h-96 = 384px, max-w-xs = 320px)
        const estimatedHeight = 384
        const estimatedWidth = 320
        const margin = 8
        
        // Determine best position based on available space
        let bestPosition = position
        const viewportHeight = window.innerHeight
        const viewportWidth = window.innerWidth
        
        // Check if default position would cause overflow and adjust
        if (position === 'top' && rect.top < estimatedHeight + margin) {
          // Not enough space on top, try bottom
          if (rect.bottom + estimatedHeight + margin < viewportHeight) {
            bestPosition = 'bottom'
          } else if (rect.left > estimatedWidth + margin) {
            bestPosition = 'left'
          } else if (rect.right + estimatedWidth + margin < viewportWidth) {
            bestPosition = 'right'
          }
        } else if (position === 'bottom' && rect.bottom + estimatedHeight + margin > viewportHeight) {
          // Not enough space on bottom, try top
          if (rect.top > estimatedHeight + margin) {
            bestPosition = 'top'
          } else if (rect.left > estimatedWidth + margin) {
            bestPosition = 'left'
          } else if (rect.right + estimatedWidth + margin < viewportWidth) {
            bestPosition = 'right'
          }
        }
        
        setActualPosition(bestPosition)
        
        let x = 0
        let y = 0
        
        switch (bestPosition) {
          case 'top':
            x = rect.left + scrollX + rect.width / 2
            y = rect.top + scrollY - margin
            break
          case 'bottom':
            x = rect.left + scrollX + rect.width / 2
            y = rect.bottom + scrollY + margin
            break
          case 'left':
            x = rect.left + scrollX - margin
            y = rect.top + scrollY + rect.height / 2
            break
          case 'right':
            x = rect.right + scrollX + margin
            y = rect.top + scrollY + rect.height / 2
            break
        }
        
        setTooltipPosition({ x, y })
        setIsVisible(true)
      }
    }, delay)
  }

  const hideTooltip = () => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
    }
    
    // Add a small delay to allow mouse to move to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      // Only hide if not hovering over either trigger or tooltip
      if (!isHoveringTrigger && !isHoveringTooltip) {
        setIsVisible(false)
        setActualPosition(position) // Reset to default position
      }
    }, 100) // 100ms delay to allow smooth mouse movement
  }

  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current)
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  // Check if we should hide the tooltip when hover states change
  useEffect(() => {
    if (!isHoveringTrigger && !isHoveringTooltip && isVisible) {
      hideTooltip()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHoveringTrigger, isHoveringTooltip, isVisible])

  // Fine-tune position after tooltip renders
  useLayoutEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      let adjustedX = tooltipPosition.x
      let adjustedY = tooltipPosition.y
      
      // Adjust horizontal position if tooltip goes off-screen
      if (actualPosition === 'top' || actualPosition === 'bottom') {
        // Check left boundary
        if (tooltipRect.left < 0) {
          adjustedX = tooltipPosition.x - tooltipRect.left + 8
        }
        // Check right boundary
        if (tooltipRect.right > viewportWidth) {
          adjustedX = tooltipPosition.x - (tooltipRect.right - viewportWidth) - 8
        }
      }
      
      // Adjust vertical position if tooltip goes off-screen
      if (actualPosition === 'left' || actualPosition === 'right') {
        // Check top boundary
        if (tooltipRect.top < 0) {
          adjustedY = tooltipPosition.y - tooltipRect.top + 8
        }
        // Check bottom boundary
        if (tooltipRect.bottom > viewportHeight) {
          adjustedY = tooltipPosition.y - (tooltipRect.bottom - viewportHeight) - 8
        }
      }
      
      // Update position if adjustments were made
      if (adjustedX !== tooltipPosition.x || adjustedY !== tooltipPosition.y) {
        setTooltipPosition({ x: adjustedX, y: adjustedY })
      }
    }
  }, [isVisible, actualPosition, tooltipPosition.x, tooltipPosition.y])

  const getTooltipClasses = () => {
    const baseClasses = `
      absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg
      max-w-xs break-words whitespace-pre-wrap
      before:content-[''] before:absolute before:w-0 before:h-0 before:border-4
      [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-800 
      [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded
    `
    
    const positionClasses = {
      top: `
        transform -translate-x-1/2 -translate-y-full
        before:top-full before:left-1/2 before:-translate-x-1/2
        before:border-t-gray-900 before:border-l-transparent before:border-r-transparent before:border-b-transparent
      `,
      bottom: `
        transform -translate-x-1/2
        before:-top-1 before:left-1/2 before:-translate-x-1/2
        before:border-b-gray-900 before:border-l-transparent before:border-r-transparent before:border-t-transparent
      `,
      left: `
        transform -translate-x-full -translate-y-1/2
        before:left-full before:top-1/2 before:-translate-y-1/2
        before:border-l-gray-900 before:border-t-transparent before:border-b-transparent before:border-r-transparent
      `,
      right: `
        transform -translate-y-1/2
        before:-left-1 before:top-1/2 before:-translate-y-1/2
        before:border-r-gray-900 before:border-t-transparent before:border-b-transparent before:border-l-transparent
      `
    }
    
    return `${baseClasses} ${positionClasses[actualPosition]} ${className}`.trim()
  }

  const tooltipElement = isVisible && content ? (
    <div
      ref={tooltipRef}
      className={getTooltipClasses()}
      style={{
        left: `${tooltipPosition.x}px`,
        top: `${tooltipPosition.y}px`,
      }}
      onMouseEnter={() => setIsHoveringTooltip(true)}
      onMouseLeave={() => {
        setIsHoveringTooltip(false)
        hideTooltip()
      }}
    >
      {content}
    </div>
  ) : null

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={() => {
          setIsHoveringTrigger(true)
          showTooltip()
        }}
        onMouseLeave={() => {
          setIsHoveringTrigger(false)
          hideTooltip()
        }}
        onFocus={() => {
          setIsHoveringTrigger(true)
          showTooltip()
        }}
        onBlur={() => {
          setIsHoveringTrigger(false)
          hideTooltip()
        }}
        className="inline-block"
      >
        {children}
      </div>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  )
}

export default Tooltip